import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { RoundedBox } from "@react-three/drei";
import * as THREE from "three";

/* ---------- Disposable vape device (Elf Bar / Vaal silhouette) ---------- */
function VapeDevice({ position, rotation = [0, 0, 0], color, glow, speed = 1, scale = 1 }) {
  const g = useRef();
  const led = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed;
    if (g.current) {
      g.current.position.y = position[1] + Math.sin(t * 0.9) * 0.35;
      g.current.rotation.x = rotation[0] + t * 0.15;
      g.current.rotation.z = rotation[2] + Math.sin(t * 0.5) * 0.15;
    }
    if (led.current) {
      // LED pulse when device is "active"
      led.current.material.emissiveIntensity = 1.5 + Math.sin(t * 3) * 0.8;
    }
  });

  return (
    <group ref={g} position={position} rotation={rotation} scale={scale}>
      {/* Body — rounded slab, matte gunmetal with a coloured tint */}
      <RoundedBox args={[0.85, 2.3, 0.42]} radius={0.16} smoothness={4} castShadow>
        <meshStandardMaterial
          color={color}
          roughness={0.35}
          metalness={0.75}
          emissive={color}
          emissiveIntensity={0.12}
        />
      </RoundedBox>
      {/* Mouthpiece at top */}
      <mesh position={[0, 1.28, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.18, 0.25, 24]} />
        <meshStandardMaterial color="#0a0a0a" roughness={0.4} metalness={0.4} />
      </mesh>
      {/* LED indicator strip near the bottom */}
      <mesh ref={led} position={[0, -0.95, 0.22]}>
        <boxGeometry args={[0.42, 0.06, 0.02]} />
        <meshStandardMaterial
          color={glow}
          emissive={glow}
          emissiveIntensity={2.2}
          toneMapped={false}
        />
      </mesh>
      {/* Digital-display window */}
      <mesh position={[0, 0.4, 0.22]}>
        <boxGeometry args={[0.45, 0.32, 0.01]} />
        <meshStandardMaterial
          color="#0a0a0a"
          emissive="#00FFC2"
          emissiveIntensity={0.35}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

/* ---------- Nicotine pouch tin (ZYN / VELO shape) ---------- */
function PouchTin({ position, color, ringColor, speed = 1 }) {
  const g = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed;
    if (g.current) {
      g.current.position.y = position[1] + Math.cos(t * 0.7) * 0.4;
      g.current.rotation.y = t * 0.55;
      g.current.rotation.x = 0.25 + Math.sin(t * 0.4) * 0.1;
    }
  });
  return (
    <group ref={g} position={position}>
      {/* Bottom half of the tin */}
      <mesh castShadow>
        <cylinderGeometry args={[0.55, 0.55, 0.35, 48]} />
        <meshStandardMaterial color={color} roughness={0.35} metalness={0.55} />
      </mesh>
      {/* Lid rim — thin bright ring */}
      <mesh position={[0, 0.185, 0]}>
        <torusGeometry args={[0.55, 0.03, 16, 64]} />
        <meshStandardMaterial
          color={ringColor}
          emissive={ringColor}
          emissiveIntensity={1.4}
          toneMapped={false}
        />
      </mesh>
      {/* Lid top */}
      <mesh position={[0, 0.2, 0]}>
        <cylinderGeometry args={[0.53, 0.53, 0.06, 48]} />
        <meshStandardMaterial color={color} roughness={0.3} metalness={0.7} />
      </mesh>
      {/* Brand disc on lid */}
      <mesh position={[0, 0.235, 0]}>
        <cylinderGeometry args={[0.28, 0.28, 0.01, 48]} />
        <meshStandardMaterial
          color="#0a0a0a"
          emissive={ringColor}
          emissiveIntensity={0.5}
        />
      </mesh>
    </group>
  );
}

/* ---------- Vapor plume — a stream of soft glowing spheres drifting upward ---------- */
function VaporPlume({ origin, tint, count = 40, speed = 1 }) {
  const ref = useRef();
  const seeds = useMemo(
    () =>
      new Array(count).fill(0).map((_, i) => ({
        offset: Math.random() * 6,
        sway: 0.2 + Math.random() * 0.35,
        radius: 0.05 + Math.random() * 0.09,
        speed: 0.5 + Math.random() * 0.6,
      })),
    [count]
  );

  useFrame((state) => {
    const t = state.clock.elapsedTime * speed;
    if (!ref.current) return;
    ref.current.children.forEach((mesh, i) => {
      const s = seeds[i];
      const local = (t * s.speed + s.offset) % 6;
      const y = origin[1] + local; // rises 0 → 6 units
      const x = origin[0] + Math.sin(local * 1.4 + s.offset) * s.sway;
      const z = origin[2] + Math.cos(local * 1.1 + s.offset) * s.sway * 0.6;
      mesh.position.set(x, y, z);
      const life = 1 - local / 6;
      // Grow gently, cap so the puffs stay small
      const scale = 0.6 + local * 0.35;
      mesh.scale.setScalar(scale);
      mesh.material.opacity = Math.max(0, life * 0.22);
    });
  });

  return (
    <group ref={ref}>
      {seeds.map((s, i) => (
        <mesh key={i}>
          <sphereGeometry args={[s.radius, 12, 12]} />
          <meshBasicMaterial
            color={tint}
            transparent
            opacity={0.22}
            depthWrite={false}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      ))}
    </group>
  );
}

/* ---------- Liquid droplet (small floating orb, like e-liquid) ---------- */
function LiquidDrop({ position, color, speed = 1 }) {
  const ref = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed;
    if (ref.current) {
      ref.current.position.y = position[1] + Math.sin(t * 1.4) * 0.7;
      ref.current.position.x = position[0] + Math.cos(t * 0.8) * 0.35;
      ref.current.rotation.y = t * 0.6;
    }
  });
  return (
    <mesh ref={ref} position={position} castShadow>
      <sphereGeometry args={[0.28, 32, 32]} />
      <meshPhysicalMaterial
        color={color}
        roughness={0.05}
        metalness={0.1}
        transmission={0.85}
        thickness={0.5}
        ior={1.4}
        emissive={color}
        emissiveIntensity={0.25}
      />
    </mesh>
  );
}

/* ---------- Scene ---------- */
export default function Hero3D() {
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={{ position: [0, 0.4, 6.2], fov: 45 }}
      style={{ position: "absolute", inset: 0 }}
      gl={{ antialias: true, alpha: false }}
    >
      <color attach="background" args={["#050505"]} />
      {/* Lighting rig — three-point neon: warm key, cool fill, punchy rim */}
      <ambientLight intensity={0.25} />
      <pointLight position={[5, 3.5, 4]} intensity={45} color="#FF5A1F" distance={22} decay={2} />
      <pointLight position={[-6, -2, 3]} intensity={38} color="#00FFC2" distance={22} decay={2} />
      <pointLight position={[0.5, 5.5, -2]} intensity={26} color="#FFC900" distance={22} decay={2} />
      <spotLight
        position={[0, 8, 6]}
        angle={0.6}
        penumbra={0.5}
        intensity={15}
        color="#ffffff"
        castShadow
      />

      <Suspense fallback={null}>
        {/* Two vape devices tilted at opposite angles */}
        <VapeDevice
          position={[-2.6, -0.1, 0.2]}
          rotation={[0.15, 0.3, -0.35]}
          color="#1c1c1c"
          glow="#FF5A1F"
          speed={0.75}
          scale={1.05}
        />
        <VapeDevice
          position={[2.7, 0.3, -0.4]}
          rotation={[0.1, -0.4, 0.4]}
          color="#111111"
          glow="#00FFC2"
          speed={0.9}
          scale={0.95}
        />

        {/* Nicotine pouch tin centre-stage */}
        <PouchTin position={[0.1, -0.6, 0.8]} color="#f5f5f2" ringColor="#00FFC2" speed={0.7} />
        {/* Second tin further back for depth */}
        <PouchTin position={[-3.6, 1.6, -1.6]} color="#0f0f0f" ringColor="#FFC900" speed={0.6} />

        {/* Liquid drops scattered around */}
        <LiquidDrop position={[3.5, 1.6, -0.8]} color="#FF5A1F" speed={0.8} />
        <LiquidDrop position={[-3.2, -1.4, 1.2]} color="#00FFC2" speed={1.1} />
        <LiquidDrop position={[1.7, 2.2, -2]} color="#FFC900" speed={0.9} />

        {/* Vapor plumes — subtle, drifting behind the devices */}
        <VaporPlume origin={[-2.6, -2.4, -1.4]} tint="#FF5A1F" count={14} speed={0.35} />
        <VaporPlume origin={[2.7, -2.2, -1.6]} tint="#00FFC2" count={14} speed={0.4} />
        <VaporPlume origin={[0, -2.6, -1.2]} tint="#FFC900" count={12} speed={0.32} />
      </Suspense>
    </Canvas>
  );
}
